import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null); // "client" | "commerce" | null
  const [user, setUser] = useState(null);
  const [commerce, setCommerce] = useState(null);
  // Public admin toggle: when false, ALL visitor-account UI must be hidden
  // (login / register / profile / history). Visitors can still take an
  // anonymous ticket via /api/tickets/anonymous.
  const [visitorAccountsEnabled, setVisitorAccountsEnabled] = useState(false);

  const refresh = useCallback(async () => {
    // Load the public visitor-accounts toggle in parallel — needed by the
    // header / home / search to hide login UI even for anonymous visitors.
    try {
      const { data: cfg } = await api.get("/system/visitor-accounts");
      setVisitorAccountsEnabled(!!cfg?.enabled);
    } catch {
      /* default stays false — safer fallback than showing broken auth UI */
    }
    let token = localStorage.getItem("bypassgo_token");
    // If no active token but a guest backup exists, restore it so the
    // anonymous visitor never loses their ticket after logging into &
    // out of an establishment account on the same browser.
    if (!token) {
      const guest = localStorage.getItem("bypassgo_guest_token");
      if (guest) {
        localStorage.setItem("bypassgo_token", guest);
        token = guest;
      }
    }
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Visitor accounts can be globally disabled by the admin (guest-only
      // mode). In that case, any legacy non-guest non-admin client token is
      // obsolete : purge it so the visitor lands in a clean anonymous state
      // (the header / home page won't show the now-irrelevant profile,
      // wrench and logout icons).
      let visitorAccountsEnabled = true;
      try {
        const cfg = await api.get("/system/visitor-accounts");
        visitorAccountsEnabled = cfg.data?.enabled !== false;
      } catch {
        /* if endpoint unreachable, leave token alone */
      }
      if (
        data.role === "client"
        && !visitorAccountsEnabled
        && !data.user?.isGuest
        && !data.user?.isAdmin
      ) {
        localStorage.removeItem("bypassgo_token");
        localStorage.removeItem("bypassgo_guest_token");
        setRole(null);
        setUser(null);
        setCommerce(null);
        return;
      }
      setRole(data.role);
      if (data.role === "client") setUser(data.user);
      else setCommerce(data.commerce);
    } catch (err) {
      // Spec fork 25 fix « tickets dupliqués » — ne purger le token QUE
      // sur un 401 explicite (auth invalide / expirée). Une erreur réseau
      // transitoire (5xx, timeout, offline) NE doit PAS détruire la
      // session : sinon le prochain clic « Prendre un ticket » va spawn
      // un nouveau guest et faire gonfler la file à tort.
      if (err?.response?.status === 401) {
        localStorage.removeItem("bypassgo_token");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginClient = async (email, password) => {
    const { data } = await api.post("/auth/login/client", { email, password });
    localStorage.setItem("bypassgo_token", data.token);
    setRole("client");
    setUser(data.user);
    setCommerce(null);
    return data;
  };

  const registerClient = async (payload) => {
    const { data } = await api.post("/auth/register/client", payload);
    localStorage.setItem("bypassgo_token", data.token);
    setRole("client");
    setUser(data.user);
    setCommerce(null);
    return data;
  };

  const loginCommerce = async (identifier, password, captcha = null) => {
    const payload = { identifier, password };
    if (captcha?.id) {
      payload.captchaId = captcha.id;
      payload.captchaAnswer = captcha.answer;
    }
    const { data } = await api.post("/auth/login/commerce", payload);
    localStorage.setItem("bypassgo_token", data.token);
    // Admin fallback: when ADMIN_EMAILS authenticates here, the backend
    // returns `role: "client"` with a `user` payload (no commerce). We
    // still wire the auth state so the admin lands in the proper space.
    if (data.role === "client") {
      setRole("client");
      setUser(data.user);
      setCommerce(null);
    } else {
      setRole("commerce");
      setCommerce(data.commerce);
      setUser(null);
    }
    return data;
  };

  const registerCommerce = async (payload) => {
    const { data } = await api.post("/auth/register/commerce", payload);
    localStorage.setItem("bypassgo_token", data.token);
    setRole("commerce");
    setCommerce(data.commerce);
    setUser(null);
    return data;
  };

  const logout = async () => {
    localStorage.removeItem("bypassgo_token");
    setRole(null);
    setUser(null);
    setCommerce(null);
    // If a guest backup token exists, restore it so the visitor recovers
    // their anonymous ticket after signing out of a merchant/admin
    // account on the same browser. Then refresh state from the API.
    const guest = localStorage.getItem("bypassgo_guest_token");
    if (guest) {
      localStorage.setItem("bypassgo_token", guest);
      await refresh();
    }
  };

  return (
    <AuthContext.Provider
      value={{ loading, role, user, commerce, visitorAccountsEnabled, loginClient, registerClient, loginCommerce, registerCommerce, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
